import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Menu, MenuItem, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { fetchBaseQueryWithEncryption } from '@/features/fetchBaseQueryWithEncryption';
import { CheckCircle, Close, FullscreenOutlined, Refresh, WarningAmber } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { agentConnectionManager } from './utils/agentConnectionManager';

type SessionRunTabProps = {
    scenarioId?: string | number | null;
    scenarioDetails?: any;
};

const SessionRunTab = ({ scenarioId, scenarioDetails }: SessionRunTabProps) => {
    const [runHistory, setRunHistory] = useState<any>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [connected, setConnected] = useState<boolean>(agentConnectionManager?.isConnected() || false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [result, setResult] = useState<string | null>(null);
    const pollingIntervalRef = useRef<any>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const safetyTimeoutRef = useRef<any>(null);
    const [selectedHistory, setSelectedHistory] = useState<any>(null);
    const [selectedKey, setSelectedKey] = useState<any>(null);
    const [loadingIframe, setLoadingIframe] = useState(false);
    const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
    const isMountedRef = useRef(true);



    const hasRemoteAgents = false;
    const statusToColor = (status?: string) => {
        if (!status) return 'default' as const;
        const s = status.toUpperCase();
        if (s.includes('SUCCESS') || s.includes('PASSED') || s.includes('COMPLETED'))
            return 'success' as const;
        if (s.includes('FAIL')) return 'error' as const;
        if (s.includes('PROGRESS') || s.includes('RUNNING')) return 'warning' as const;
        return 'default' as const;
    };

    const handleRunRemote = () => { }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        const unsubscribe = agentConnectionManager?.subscribe(setConnected);
        return unsubscribe;
    }, []);

    const apiBaseUrl = useMemo(() => import.meta.env.VITE_EXPRESS_API_URL, []);

    const formatDateTime = (value?: string | number | null) => {
        if (!value) {
            return '—';
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return String(value);
        }

        return parsed.toLocaleString();
    };

    const formatDuration = (value?: number | string | null) => {
        if (!value && value !== 0) {
            return '—';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (value < 1000) {
            return `${value} ms`;
        }

        const seconds = Math.round(value / 1000);
        return `${seconds}s`;
    };

    useEffect(() => {
        // Only process data once we have a response from the API (success or empty)
        if (runHistory !== undefined) {
            if (Array.isArray(runHistory) && runHistory?.length > 0) {
                // Find the appropriate report to show
                const reports = runHistory;

                // First, check if the latest report is in progress
                const latestReport = reports[0];

                if (latestReport?.status === 'IN_PROGRESS') {
                    // If latest is in progress, find the last completed report
                    const completedReport = reports.find(
                        (report) =>
                            report.status === 'SUCCESS' ||
                            report.status === 'FAILED' ||
                            report.status === 'GENERATED' ||
                            report.report?.success === true ||
                            report.report?.success === false
                    );

                    if (completedReport) {
                        setSelectedHistory(completedReport);
                        setSelectedKey(completedReport._id);
                        setResult(completedReport);
                    } else {
                        // No completed report found, show the latest (in progress)
                        setSelectedHistory(latestReport);
                        setSelectedKey(latestReport._id);
                        setResult(latestReport);
                    }
                } else {
                    // Latest report is completed, show it
                    setSelectedHistory(latestReport);
                    setSelectedKey(latestReport._id);
                    setResult(latestReport);
                }
            } else {
                setSelectedHistory(null);
                setSelectedKey(0);
                setResult(null);
            }
        }
    }, [runHistory]);

    const lastReport = useMemo(() => {
        if (!runHistory.length) {
            return null;
        }

        const sorted = [...runHistory].sort((a, b) => {
            const aTime = new Date(a?.createdAt || a?.updatedAt || 0).getTime();
            const bTime = new Date(b?.createdAt || b?.updatedAt || 0).getTime();
            return bTime - aTime;
        });

        return sorted[0];
    }, [runHistory]);

    const fetchRunHistory = useCallback(async () => {
        if (!apiBaseUrl || !scenarioId || !scenarioDetails?.itemName) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        try {
            const url = `${apiBaseUrl}/reports?testScenario=${encodeURIComponent(
                String(scenarioId)
            )}&type=TEST_SCENARIO&reportFrom=EXECUTION`;
            const response = await fetchBaseQueryWithEncryption(url, {
                method: 'GET',
            });

            const responseData = (response as any)?.items as any;
            const normalized =
                responseData?.decryptedData ?? responseData?.localData ?? responseData ?? [];
            const list = Array.isArray(normalized?.data)
                ? normalized.data
                : Array.isArray(normalized)
                    ? normalized
                    : [];

            if (isMountedRef.current) {
                setRunHistory(list);
            }
        } catch (error) {
            if (isMountedRef.current) {
                setErrorMessage(
                    error instanceof Error ? error.message : 'Failed to load run history.'
                );
            }
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [apiBaseUrl, scenarioId, scenarioDetails?.itemName]);

    useEffect(() => {
        fetchRunHistory();
    }, [fetchRunHistory]);

    const reportFields = useMemo(
        () => [
            { label: 'Report ID', value: lastReport?._id ?? lastReport?.id },
            { label: 'Status', value: lastReport?.report?.success ? 'SUCCESS' : lastReport?.report?.success === false ? 'FAILED' : lastReport?.status ?? lastReport?.result ?? lastReport?.state },
            { label: 'Started', value: formatDateTime(lastReport?.startedAt ?? lastReport?.createdAt) },
            { label: 'Finished', value: formatDateTime(lastReport?.finishedAt ?? lastReport?.updatedAt) },
            { label: 'Duration', value: formatDuration(lastReport?.durationMs ?? lastReport?.duration) },
            { label: 'Total Steps', value: lastReport?.totalSteps ?? lastReport?.stepsCount ?? lastReport?.total },
            { label: 'Passed', value: lastReport?.passed ?? lastReport?.success },
            { label: 'Failed', value: lastReport?.failed ?? lastReport?.errors },
            { label: 'Skipped', value: lastReport?.skipped },
        ],
        [lastReport]
    );

    const type: any = 'TEST_SCENARIO';
    const testCaseLabel: any = scenarioDetails?.itemName;
    const testContent: any = scenarioDetails?.testContent;
    const datasetId: any = scenarioDetails?.datasetId;

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        if (safetyTimeoutRef.current) {
            clearTimeout(safetyTimeoutRef.current);
            safetyTimeoutRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            stopPolling();
        };
    }, []);

    const sortedHistory = useMemo(() => {
        if (!Array.isArray(runHistory)) {
            return [];
        }

        return [...runHistory].sort((a, b) => {
            const aTime = new Date(a?.createdAt || a?.updatedAt || 0).getTime();
            const bTime = new Date(b?.createdAt || b?.updatedAt || 0).getTime();
            return bTime - aTime;
        });
    }, [runHistory]);

    const handleOpenRunHistory = () => {
        setIsHistoryDrawerOpen(true);
        fetchRunHistory();
    };

    const handleCloseRunHistory = () => {
        setIsHistoryDrawerOpen(false);
    };

    const handleSelectHistory = (history: any) => {
        setSelectedHistory(history);
        setSelectedKey(history?._id ?? history?.id ?? Date.now());
        setResult(history);
    };

    const startPollingReport = (reportId: string) => {
        stopPolling();
        pollingIntervalRef.current = setInterval(async () => {
            try {
                // Cancel previous in-flight request if any
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
                const controller = new AbortController();
                abortControllerRef.current = controller;
                const res = await fetch(
                    `${import.meta.env.VITE_EXPRESS_API_URL
                    }/reports/${reportId}?t=${Date.now()}`,
                    {
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                    }
                );
                if (!res.ok) return;
                const reportData = await res.json();
                const status = reportData?.status;
                setSelectedHistory(reportData);
                setSelectedKey(reportData?._id);
                if (status !== 'IN_PROGRESS') {
                    stopPolling();
                    setIsRunning(false);
                }
            } catch (err) {
                // Ignore abort errors; on real errors, we keep polling
            }
        }, 1000);
    };

    const runPlaywrightCode = async () => {
        setIsRunning(true);
        try {
            if (false) {

            } else {
                let url = `${import.meta.env.VITE_ATA_AGENT_BASE_URL
                    }/selenium/upload-and-run-playwright?`;
                if (type === 'TEST_CASE') {
                    url += `testCase=${testCaseLabel}&testScenario=${scenarioDetails?._id}&testSuite=${scenarioDetails?.folder?._id || scenarioDetails?.folder
                        }&application=${scenarioDetails?.apiCollection?._id}&type=${type}`;
                } else if (type === 'TEST_SCENARIO') {
                    url += `testScenario=${scenarioDetails?._id}&testSuite=${scenarioDetails?.folder?._id || scenarioDetails?.folder
                        }&application=${scenarioDetails?.apiCollection?._id}&type=${type}`;
                }

                // else if (type === 'TEST_SUITE') {
                //     url += `testSuite=${requestData?._id}&application=${requestData?.apiCollection?._id}&type=${type}`;
                // } else if (type === 'APPLICATION') {
                //     url += `application=${requestData?._id}&type=${type}`;
                // }
                const response = await fetch(url, {
                    method: 'POST',
                    body: JSON.stringify({
                        headed: true,
                        ...(type === 'TEST_CASE' && { testContent: testContent }),
                        ...(datasetId && { dataSet: datasetId }),
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                const data = await response.json();
                const reportId =
                    data?.reportId ||
                    data?.id ||
                    data?._id ||
                    data?.data?.reportId ||
                    data?.data?.id ||
                    data?.data?._id;
                setResult(JSON.stringify({ started: true, reportId, runResponse: data }, null, 2));
                if (reportId) {
                    startPollingReport(reportId);
                } else {
                    toast.error('Failed to get report id or open the ata-agent');
                    setIsRunning(false);
                }
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to run code or open the ata-agent');
            stopPolling();
            setIsRunning(false);
        }
    };


    const handleRunLocal = () => {
        handleMenuClose();
        runPlaywrightCode();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Run</Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        onMouseEnter={handleMenuOpen}
                        id="snr-ai-UITestingDetail-runButton"
                        disabled={isRunning}
                        sx={{ position: 'relative' }}
                    >
                        {isRunning ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Running...
                            </>
                        ) : (
                            'Run'
                        )}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            onMouseLeave: handleMenuClose,
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <Tooltip title={!connected ? "Local agent is not running" : ""} placement="right">
                            <MenuItem onClick={handleRunLocal} disabled={isRunning}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {connected ? (
                                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                    ) : (
                                        <WarningAmber sx={{ fontSize: 18, color: 'warning.main' }} />
                                    )}
                                    <Typography
                                        sx={{
                                            filter: !connected ? 'blur(0.5px)' : 'none',
                                            opacity: !connected ? 0.6 : 1,
                                        }}
                                    >
                                        Run in Local
                                    </Typography>
                                </Box>
                            </MenuItem>
                        </Tooltip>
                        <Tooltip title={!hasRemoteAgents ? "No remote agents available" : ""} placement="right">
                            <MenuItem onClick={handleRunRemote} disabled={isRunning || !hasRemoteAgents}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {hasRemoteAgents ? (
                                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                    ) : (
                                        <WarningAmber sx={{ fontSize: 18, color: 'warning.main' }} />
                                    )}
                                    <Typography
                                        sx={{
                                            filter: !hasRemoteAgents ? 'blur(0.5px)' : 'none',
                                            opacity: !hasRemoteAgents ? 0.6 : 1,
                                        }}
                                    >
                                        Run in Cloud
                                    </Typography>
                                </Box>
                            </MenuItem>
                        </Tooltip>
                    </Menu>
                    <Button variant="outlined" onClick={handleOpenRunHistory}>
                        Run History
                    </Button>
                </Stack>
            </Box>

            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            {isLoading ? (
                <Card>
                    <CardContent>
                        <Stack spacing={1}>
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="75%" />
                            <Skeleton variant="text" width="30%" />
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {!isLoading && !lastReport ? (
                <Alert severity="info">No run history found yet.</Alert>
            ) : null}

            {!isLoading && lastReport ? (
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Last Run Report
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1}>
                            {reportFields
                                .filter((field) => field.value !== undefined && field.value !== null)
                                .map((field) => (
                                    <InfoRow
                                        key={field.label}
                                        label={field.label}
                                        value={field.value}
                                    />
                                ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            <Box
                sx={{
                    height: '100%',
                    boxShadow: 1,
                    borderRadius: 1,
                }}
            >
                <Box
                    sx={{
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            px: 1,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            borderRadius: 1,
                            width: '100%',
                        }}
                    >
                        <Typography sx={{ fontWeight: 'bold' }}>
                            Execution Result
                        </Typography>
                        {isRunning ? <CircularProgress size={18} /> : null}
                        {typeof result === 'object' && (
                            <>
                                {((result as any)?.report?.finalResult ||
                                    (result as any)?.status) && (
                                        // <Chip
                                        //     size="small"
                                        //     color={statusToColor(
                                        //         selectedHistory?.status ||
                                        //         (result as any)?.report?.finalResult
                                        //     )}
                                        //     label={(
                                        //         (
                                        //             selectedHistory?.status ||
                                        //             (result as any)?.report?.finalResult
                                        //         )?.replace('_', ' ') ||
                                        //         selectedHistory?.status ||
                                        //         (result as any)?.report?.finalResult
                                        //     )}
                                        //     sx={{ minWidth: 80 }}
                                        // />

                                        <Chip
                                            size="small"
                                            color={statusToColor(
                                                selectedHistory.report?.success
                                                    ? 'SUCCESS'
                                                    : selectedHistory.status === 'IN_PROGRESS'
                                                        ? 'IN PROGRESS'
                                                        : 'FAILED'
                                            )}
                                            label={
                                                selectedHistory.report?.success
                                                    ? 'SUCCESS'
                                                    : selectedHistory.status === 'IN_PROGRESS'
                                                        ? 'IN PROGRESS'
                                                        : 'FAILED'
                                            }
                                        />
                                    )}
                                {/* {(result as any)?.createdAt &&
                                                    (result as any)
                                                        ?.updatedAt && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={`Duration: ${result?.report?.jsonReport?.stats?.duration ? Math.(result?.report?.jsonReport?.stats?.duration/1000) : '-'}s`}
                                                        />
                                                    )}
                                                {(result as any)?.createdAt && (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`Last: ${dayjs(
                                                            (result as any)
                                                                .createdAt
                                                        ).format(
                                                            `${dateFormat} h:mm a`
                                                        )}`}
                                                    />
                                                )} */}
                            </>
                        )}
                        <Box flex={1} />
                        <Box display="flex" gap={1} alignItems="center">
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Execution Time:{' '}
                            </Typography>
                            <Typography>
                                {/* {dayjs(
                                    tryParseJson(
                                        selectedHistory?.report?.jsonReport
                                    )?.stats?.startTime ||
                                    selectedHistory?.createdAt
                                ).format(`${dateFormat} h:mm:ss a`) || 'N/A'} */}
                            </Typography>
                            <Tooltip title="Report Fullscreen">
                                <IconButton
                                    size="small"
                                    onClick={() => { }}
                                >
                                    <FullscreenOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>
                </Box>
                <Box
                    sx={{
                        p: 1,
                    }}
                >
                    {isLoading ? (
                        <CircularProgress size={18} />
                    ) : (
                        <>
                            {selectedHistory?.reportUrl ? (
                                <Box height="80vh" overflow="auto">
                                    <>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ mb: 1 }}
                                            onClick={() =>
                                                setSelectedKey(
                                                    (prev: any) => prev + 1
                                                )
                                            }
                                        >
                                            Back
                                        </Button>
                                        {loadingIframe && <CircularProgress size={18} />}
                                        {selectedHistory?.reportUrl?.includes(
                                            'amazonaws.com'
                                        ) ? (
                                            <iframe
                                                key={selectedKey}
                                                src={selectedHistory?.reportUrl}
                                                width="100%"
                                                height="100%"
                                                onLoadStart={() =>
                                                    setLoadingIframe(true)
                                                }
                                                onLoad={() =>
                                                    setLoadingIframe(false)
                                                }
                                            // style={{
                                            //     colorScheme:
                                            //         theme.palette.mode ===
                                            //             'dark'
                                            //             ? 'dark'
                                            //             : 'light',
                                            //     // @ts-ignore
                                            //     '--color-scheme':
                                            //         theme.palette.mode ===
                                            //             'dark'
                                            //             ? 'dark'
                                            //             : 'light',
                                            // }}
                                            />
                                        ) : (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                height="100%"
                                            >
                                                <Typography>
                                                    Report is not available
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                </Box>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%" border={1} borderColor="divider" borderRadius={1} p={2}>
                                    <Typography>
                                        Report is not available
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
            <Drawer
                anchor="right"
                open={isHistoryDrawerOpen}
                onClose={handleCloseRunHistory}
                PaperProps={{ sx: { width: 360, maxWidth: '90vw' } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                        Run History
                    </Typography>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                size="small"
                                onClick={fetchRunHistory}
                                disabled={isLoading}
                            >
                                <Refresh fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" onClick={handleCloseRunHistory}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
                <Divider />
                <Box sx={{ px: 1, py: 1 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : null}
                    {!isLoading && !sortedHistory.length ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 1, py: 2, textAlign: 'center' }}
                        >
                            No run history found yet.
                        </Typography>
                    ) : null}
                    <List dense disablePadding>
                        {sortedHistory.map((history) => (
                            <ListItemButton
                                key={history?._id ?? history?.id}
                                // selected={
                                //     selectedHistory?._id === history?._id ||
                                //     selectedHistory?.id === history?.id
                                // }
                                onClick={() => handleSelectHistory(history)}
                                sx={{ borderRadius: 1, mx: 0.5, my: 0.5 }}
                            >
                                <ListItemText
                                    primary={
                                        history?.status ||
                                        history?.result ||
                                        history?.state ||
                                        'Unknown Status'
                                    }
                                    secondary={formatDateTime(
                                        history?.startedAt ?? history?.createdAt
                                    )}
                                />
                                <Chip
                                    size="small"
                                    color={statusToColor(
                                        history?.report?.success ? 'SUCCESS' : 'FAILED'
                                    )}
                                    label={(
                                        history?.report?.success ? 'SUCCESS' : 'FAILED'
                                    )}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </Box>
    );
};

export default SessionRunTab;

type InfoRowProps = {
    label: string;
    value?: ReactNode;
};

const InfoRow = ({ label, value }: InfoRowProps) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value ?? '—'}
        </Typography>
    </Box>
);